import { matchPath } from "react-router-dom";

import type { Id, MessageContext, Screen } from "../api/types";
import { addByHandPath, OPEN_POINTS_PATH, uploadPath } from "./paths";

/*
  How the names of screens in the contracts (`Screen`, 2.4.1) meet the addresses of the app (2.11.2).
  Used for "point to" (open the screen the agent named) and for the heartbeat (which screen a person is on).
*/

const SCREEN_OF_PATTERN: ReadonlyArray<readonly [pattern: string, screen: string]> = [
  ["/", "home"],
  // The views of the 2026-11-03 design. The contract has no name for them yet, so the agent gets none (`agentScreenOf`).
  ["/overview", "overview"],
  ["/items", "items"],
  ["/shared", "shared"],
  ["/capabilities", "capabilities"],
  ["/deliverables", "deliverables"],
  ["/review", "review"],
  ["/getting-started", "getting_started"],
  ["/live", "live_view"],
  ["/navigate", "family_navigator"],
  ["/gallery", "gallery"],
  ["/whats-new", "whats_new"],
  ["/sessions/new", "new_session"],
  ["/sessions/:id", "live_map"],
  ["/sessions/:id/log", "session_log"],
  ["/sessions/:id/checks", "checks_list"],
  ["/sessions/:id/coverage/:reportId", "coverage_report"],
  ["/sessions/:id/hand-overs/:hoId", "hand_over"],
  ["/items/:id/map", "item_map"],
  ["/items/:id", "item_page"],
  ["/restore", "restore_control"],
  ["/elements/new", "add_by_hand_form"],
  ["/elements/:id/edit", "edit_element"],
  ["/elements/:id", "element_page"],
  ["/symbols", "symbol_picker"],
  ["/deliverables/new", "upload_form"],
  ["/deliverables/:id", "deliverable_view"],
  ["/library", "library_browser"],
  ["/files", "files_view"],
  ["/chapters", "chapters"],
  ["/chapters/:key", "chapter_editor"],
  ["/project-lists", "project_lists"],
  ["/open-points", "open_points"],
  ["/inbox", "sme_inbox"],
  ["/inbox/:elementId", "sme_inbox"],
  ["/back-from-sme", "back_from_sme"],
  ["/onboarding", "onboarding"],
  ["/profile", "my_profile"],
  ["/admin", "admin_accounts"],
  ["/change-password", "change_password"],
  ["/sign-in", "sign_in"],
];

export interface RouteLocation {
  screen: string;
  itemId: Id | null;
  sessionId: Id | null;
}

/** Which screen an address is, and which item and session it names. */
export function locate(pathname: string): RouteLocation {
  for (const [pattern, screen] of SCREEN_OF_PATTERN) {
    const match = matchPath({ path: pattern, end: true }, pathname);
    if (!match) continue;
    const id = match.params.id ?? null;
    return {
      screen,
      itemId: pattern.startsWith("/items/") ? id : null,
      sessionId: pattern.startsWith("/sessions/") && id !== "new" ? id : null,
    };
  }
  return { screen: pathname, itemId: null, sessionId: null };
}

/** Every screen the agent can be told about (the contract's `Screen`). The record makes a new value a type error here. */
const AGENT_SCREENS: Record<Screen, true> = {
  item_page: true,
  item_map: true,
  live_map: true,
  live_view: true,
  family_navigator: true,
  gallery: true,
  library_browser: true,
  deliverable_view: true,
  proposal_tray: true,
  checks_list: true,
  files_view: true,
  sme_inbox: true,
  restore_control: true,
  symbol_picker: true,
  add_by_hand_form: true,
  admin_accounts: true,
  admin_model_connection: true,
  admin_disciplines: true,
  upload_form: true,
  open_points: true,
  chapters: true,
  project_lists: true,
};

/**
 * The name the agent knows an address by, or null when the contract has none (the home, what's new, the sign-in). The
 * chapter editor is a chapter for the agent, as `pointToPath` already reads it.
 */
export function agentScreenOf(pathname: string): Screen | null {
  const name = locate(pathname).screen;
  if (name === "chapter_editor") return "chapters";
  return Object.prototype.hasOwnProperty.call(AGENT_SCREENS, name) ? (name as Screen) : null;
}

/** What a message to the agent says about where the person is: the screen, and the item or element on it. */
export function agentContextFor(pathname: string): MessageContext {
  const here = locate(pathname);
  const element = matchPath({ path: "/elements/:id", end: true }, pathname)?.params.id ?? null;
  const context: MessageContext = { screen: agentScreenOf(pathname) };
  const elementId = here.itemId ?? element;
  if (elementId) context.element_id = elementId;
  return context;
}

interface PointTarget {
  screen: Screen;
  element_id?: Id | null;
}

/**
 * The address of a screen the agent pointed to, or null when the pointer names nothing that has an
 * address of its own (a map without an item, a session tray with no session open).
 */
export function pointToPath(target: PointTarget, context: { sessionId?: Id | null } = {}): string | null {
  const id = target.element_id ? encodeURIComponent(target.element_id) : null;
  const session = context.sessionId ? encodeURIComponent(context.sessionId) : null;
  switch (target.screen) {
    case "item_page":
      return id ? `/items/${id}` : null;
    case "item_map":
      return id ? `/items/${id}/map` : null;
    case "live_map":
      return session ? `/sessions/${session}` : id ? `/items/${id}/map` : null;
    case "proposal_tray":
      return session ? `/sessions/${session}` : null;
    case "checks_list":
      return session ? `/sessions/${session}/checks` : null;
    // The navigator and the live view open on the position the agent named, when it named one (workflow 22).
    case "live_view":
      return id ? `/live?select=${id}` : "/live";
    case "family_navigator":
      return id ? `/navigate?select=${id}` : "/navigate";
    case "gallery":
      return "/gallery";
    case "library_browser":
      return "/library";
    case "deliverable_view":
      return id ? `/deliverables/${id}` : null;
    case "files_view":
      return "/files";
    case "sme_inbox":
      return id ? `/inbox/${id}` : "/inbox";
    case "restore_control":
      return "/restore";
    case "symbol_picker":
      return "/symbols";
    // The session goes with the form, so the agent learns of what is added and the way back is offered (CP-06).
    case "add_by_hand_form":
      return addByHandPath({ session: context.sessionId });
    case "admin_accounts":
      return "/admin#accounts";
    case "admin_model_connection":
      return "/admin#model-connection";
    case "admin_disciplines":
      return "/admin#disciplines";
    // The four screens of the completion round (CP-17). A chapter with an element opens that element's page; without one
    // the chapters index (`/chapters`, features/navigate) opens.
    case "upload_form":
      return uploadPath(context.sessionId);
    case "open_points":
      return OPEN_POINTS_PATH;
    case "chapters":
      return id ? `/elements/${id}` : "/chapters";
    case "project_lists":
      return "/project-lists";
  }
}
